import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Plus, Trash2, ShieldBan, Search, Users, Mail, ShieldCheck, Package, UserPlus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

type SongOrder = Tables<"song_orders">;

interface BlockedCustomer {
  id: string;
  email: string;
  reason: string | null;
  blocked_by: string;
  created_at: string;
}

interface CustomerRecord {
  user_id: string;
  email: string;
  full_name: string | null;
  order_count: number;
  total_spent: number;
  last_order: string;
  is_blocked: boolean;
}

const AdminCustomers = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const [orders, setOrders] = useState<SongOrder[]>([]);
  const [blocked, setBlocked] = useState<BlockedCustomer[]>([]);
  const [profiles, setProfiles] = useState<{ user_id: string; full_name: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [adminUserIds, setAdminUserIds] = useState<Set<string>>(new Set());

  // Block dialog (the "Block Customer" top-level button)
  const [blockOpen, setBlockOpen] = useState(false);
  const [newBlockEmail, setNewBlockEmail] = useState("");
  const [newBlockReason, setNewBlockReason] = useState("");
  const [blockConfirmTop, setBlockConfirmTop] = useState("");

  // Add customer dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [addingCustomer, setAddingCustomer] = useState(false);

  // Block dialog (inline from row)
  const [blockTarget, setBlockTarget] = useState<{ email: string } | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockConfirm, setBlockConfirm] = useState("");

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<{ user_id: string; email: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);

  // User emails cache (fetched via edge function)
  const [userEmails, setUserEmails] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!authLoading && !adminLoading) {
      if (!user) navigate("/login");
      else if (!isAdmin) navigate("/dashboard");
    }
  }, [user, isAdmin, authLoading, adminLoading, navigate]);

  const fetchData = async () => {
    if (!user || !isAdmin) return;

    const [ordersRes, blockedRes, profilesRes, emailsRes, adminRolesRes] = await Promise.all([
      supabase.from("song_orders").select("*").neq("status", "pending_payment").order("created_at", { ascending: false }),
      supabase.from("blocked_customers").select("*").order("created_at", { ascending: false }),
      supabase.from("profiles").select("user_id, full_name"),
      supabase.functions.invoke("get-admin-emails", { body: { type: "all_customers" } }),
      supabase.from("user_roles").select("user_id").eq("role", "admin"),
    ]);

    if (ordersRes.data) setOrders(ordersRes.data);
    if (blockedRes.data) setBlocked(blockedRes.data as BlockedCustomer[]);
    if (profilesRes.data) setProfiles(profilesRes.data);
    if (adminRolesRes.data) setAdminUserIds(new Set(adminRolesRes.data.map((r: any) => r.user_id)));

    // Build email map from the edge function response
    if (emailsRes.data?.emails) {
      const map: Record<string, string> = {};
      for (const entry of emailsRes.data.emails) {
        map[entry.id] = entry.email;
      }
      setUserEmails(map);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [user, isAdmin]);

  // Build customer list from all users
  const customers = useMemo(() => {
    const blockedEmails = new Set(blocked.map((b) => b.email.toLowerCase()));
    const profileMap = new Map(profiles.map((p) => [p.user_id, p.full_name]));

    // Build order stats per user
    const orderStats = new Map<string, { count: number; spent: number; last: string }>();
    for (const order of orders) {
      const existing = orderStats.get(order.user_id);
      if (existing) {
        existing.count += 1;
        existing.spent += Number(order.price_paid) || 0;
        if (order.created_at > existing.last) existing.last = order.created_at;
      } else {
        orderStats.set(order.user_id, {
          count: 1,
          spent: Number(order.price_paid) || 0,
          last: order.created_at,
        });
      }
    }

    // Build from all known users (from email map), excluding admins
    const result: CustomerRecord[] = [];
    for (const [userId, email] of Object.entries(userEmails)) {
      if (adminUserIds.has(userId)) continue; // Skip admin users
      const stats = orderStats.get(userId);
      if (!stats) continue; // Only show users with paid orders
      result.push({
        user_id: userId,
        email,
        full_name: profileMap.get(userId) || null,
        order_count: stats.count,
        total_spent: stats.spent,
        last_order: stats.last,
        is_blocked: blockedEmails.has(email.toLowerCase()),
      });
    }

    return result.sort((a, b) => (b.last_order || "").localeCompare(a.last_order || ""));
  }, [orders, blocked, profiles, userEmails, adminUserIds]);

  const filtered = customers.filter(
    (c) =>
      !searchQuery ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.full_name && c.full_name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const activeCustomers = filtered.filter((c) => !c.is_blocked);
  const blockedCustomers = filtered.filter((c) => c.is_blocked);

  // Also include blocked entries that don't have orders
  const blockedOnlyEntries = blocked.filter(
    (b) => !customers.some((c) => c.email.toLowerCase() === b.email.toLowerCase())
  ).filter(
    (b) =>
      !searchQuery ||
      b.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (b.reason && b.reason.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const handleBlock = async (email: string, reason: string) => {
    const { error } = await supabase.from("blocked_customers").insert({
      email: email.trim().toLowerCase(),
      reason: reason.trim() || null,
      blocked_by: user!.id,
    } as any);
    if (error) {
      if (error.code === "23505") toast.error("Already blocked.");
      else toast.error("Failed to block customer.");
    } else {
      toast.success(`${email} has been blocked.`);
      setBlockTarget(null);
      setBlockReason("");
      setBlockOpen(false);
      setNewBlockEmail("");
      setNewBlockReason("");
      setBlockConfirmTop("");
      fetchData();
    }
  };

  const handleUnblock = async (email: string) => {
    const entry = blocked.find((b) => b.email.toLowerCase() === email.toLowerCase());
    if (!entry) return;
    const { error } = await supabase.from("blocked_customers").delete().eq("id", entry.id);
    if (error) {
      toast.error("Failed to unblock.");
    } else {
      toast.success("Customer unblocked.");
      fetchData();
    }
  };

  const handleAddCustomer = async () => {
    setAddingCustomer(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-customer", {
        body: { action: "create", email: newEmail.trim(), password: newPassword },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to create customer.");
      } else {
        toast.success(`Customer ${newEmail} created.`);
        setAddOpen(false);
        setNewEmail("");
        setNewPassword("");
        fetchData();
      }
    } catch {
      toast.error("Failed to create customer.");
    }
    setAddingCustomer(false);
  };

  const handleDeleteCustomer = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("manage-customer", {
        body: { action: "delete", user_id: deleteTarget.user_id },
      });
      if (error || data?.error) {
        toast.error(data?.error || "Failed to delete customer.");
      } else {
        toast.success(`${deleteTarget.email} has been permanently deleted.`);
        setDeleteTarget(null);
        setDeleteConfirm("");
        fetchData();
      }
    } catch {
      toast.error("Failed to delete customer.");
    }
    setDeleting(false);
  };

  if (authLoading || adminLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate("/admin")}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                Customers
              </h1>
              <p className="text-muted-foreground">
                View all customers, block or unblock access to ordering.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => setAddOpen(true)}>
                <UserPlus className="w-4 h-4 mr-2" /> Add Customer
              </Button>
              <Button variant="hero" onClick={() => setBlockOpen(true)}>
                <ShieldBan className="w-4 h-4 mr-2" /> Block Customer
              </Button>
            </div>
          </div>

          {/* Block Customer Dialog (top-level) */}
          <Dialog open={blockOpen} onOpenChange={(open) => { if (!open) { setBlockOpen(false); setBlockConfirmTop(""); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Block a Customer</DialogTitle>
                <DialogDescription>
                  Enter the customer's email. They'll see an explicit message when trying to order.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label htmlFor="block-email">Email Address</Label>
                  <Input id="block-email" value={newBlockEmail} onChange={(e) => setNewBlockEmail(e.target.value)} placeholder="customer@example.com" type="email" />
                </div>
                <div>
                  <Label htmlFor="block-reason">Reason (optional)</Label>
                  <Textarea id="block-reason" value={newBlockReason} onChange={(e) => setNewBlockReason(e.target.value)} placeholder="Why are you blocking this customer?" rows={3} />
                </div>
                <div>
                  <Label htmlFor="block-confirm-top">Type <span className="font-bold text-destructive">BLOCK</span> to confirm</Label>
                  <Input id="block-confirm-top" value={blockConfirmTop} onChange={(e) => setBlockConfirmTop(e.target.value)} placeholder="BLOCK" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setBlockOpen(false); setBlockConfirmTop(""); }}>Cancel</Button>
                <Button variant="destructive" onClick={() => { handleBlock(newBlockEmail, newBlockReason); setBlockConfirmTop(""); }} disabled={!newBlockEmail.trim() || blockConfirmTop !== "BLOCK"}>
                  <ShieldBan className="w-4 h-4 mr-2" /> Block
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Customer Dialog */}
          <Dialog open={addOpen} onOpenChange={(open) => { if (!open) { setAddOpen(false); setNewEmail(""); setNewPassword(""); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add a Customer</DialogTitle>
                <DialogDescription>
                  Create a new customer account with email and password.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label htmlFor="add-email">Email Address</Label>
                  <Input id="add-email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="customer@example.com" type="email" />
                </div>
                <div>
                  <Label htmlFor="add-password">Password</Label>
                  <Input id="add-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimum 6 characters" type="password" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setAddOpen(false); setNewEmail(""); setNewPassword(""); }}>Cancel</Button>
                <Button onClick={handleAddCustomer} disabled={!newEmail.trim() || newPassword.length < 6 || addingCustomer}>
                  {addingCustomer ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  Create Account
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[
              { label: "Total Customers", value: customers.length, icon: Users, color: "text-primary" },
              { label: "Active", value: customers.filter((c) => !c.is_blocked).length, icon: ShieldCheck, color: "text-primary" },
              { label: "Blocked", value: blocked.length, icon: ShieldBan, color: "text-destructive" },
            ].map((stat) => (
              <div key={stat.label} className="bg-card rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                    <p className="text-muted-foreground text-sm">{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search customers..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" aria-label="Search customers by name or email" />
          </div>

          <Tabs defaultValue="all" className="space-y-6">
            <TabsList>
              <TabsTrigger value="all">All ({activeCustomers.length})</TabsTrigger>
              <TabsTrigger value="blocked">Blocked ({blockedCustomers.length + blockedOnlyEntries.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all">
              {activeCustomers.length === 0 ? (
                <EmptyState message="No customers found." />
              ) : (
                <div className="bg-card rounded-2xl shadow-soft overflow-hidden divide-y divide-border">
                  {activeCustomers.map((c) => (
                    <CustomerRow key={c.user_id} customer={c} onBlock={(email) => { setBlockTarget({ email }); setBlockReason(""); setBlockConfirm(""); }} onDelete={(uid, email) => { setDeleteTarget({ user_id: uid, email }); setDeleteConfirm(""); }} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="blocked">
              {blockedCustomers.length === 0 && blockedOnlyEntries.length === 0 ? (
                <EmptyState message="No blocked customers." />
              ) : (
                <div className="bg-card rounded-2xl shadow-soft overflow-hidden divide-y divide-border">
                  {blockedCustomers.map((c) => (
                    <CustomerRow key={c.user_id} customer={c} onUnblock={handleUnblock} onDelete={(uid, email) => { setDeleteTarget({ user_id: uid, email }); setDeleteConfirm(""); }} blocked />
                  ))}
                  {blockedOnlyEntries.map((b) => (
                    <div key={b.id} className="flex items-center justify-between p-5">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-foreground">{b.email}</p>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/15 text-destructive border border-destructive/30">Blocked</span>
                        </div>
                        {b.reason && <p className="text-sm text-muted-foreground mt-1">{b.reason}</p>}
                        <p className="text-xs text-muted-foreground mt-1">Blocked {new Date(b.created_at).toLocaleDateString()} · No orders</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => handleUnblock(b.email)}>
                        <ShieldCheck className="w-4 h-4 mr-2" /> Unblock
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />

      {/* Block confirm dialog */}
      <Dialog open={!!blockTarget} onOpenChange={(open) => { if (!open) { setBlockTarget(null); setBlockConfirm(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Block {blockTarget?.email}?</DialogTitle>
            <DialogDescription>They'll see a restriction message when trying to place orders.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
             <div>
              <Label htmlFor="inline-block-reason">Reason (optional)</Label>
              <Textarea id="inline-block-reason" value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder="Why are you blocking this customer?" rows={3} />
            </div>
            <div>
              <Label htmlFor="inline-block-confirm">Type <span className="font-bold text-destructive">BLOCK</span> to confirm</Label>
              <Input id="inline-block-confirm" value={blockConfirm} onChange={(e) => setBlockConfirm(e.target.value)} placeholder="BLOCK" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBlockTarget(null); setBlockConfirm(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={() => { blockTarget && handleBlock(blockTarget.email, blockReason); setBlockConfirm(""); }} disabled={blockConfirm !== "BLOCK"}>
              <ShieldBan className="w-4 h-4 mr-2" /> Block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteConfirm(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently Delete {deleteTarget?.email}?</DialogTitle>
            <DialogDescription>
              This will permanently remove the user account and ALL their data (orders, uploads, reviews, messages). This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="delete-confirm">Type <span className="font-bold text-destructive">DELETE</span> to confirm</Label>
            <Input id="delete-confirm" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirm(""); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteCustomer} disabled={deleteConfirm !== "DELETE" || deleting}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CustomerRow = ({
  customer,
  onBlock,
  onUnblock,
  onDelete,
  blocked,
}: {
  customer: CustomerRecord;
  onBlock?: (email: string) => void;
  onUnblock?: (email: string) => void;
  onDelete?: (userId: string, email: string) => void;
  blocked?: boolean;
}) => (
  <div className="flex items-center justify-between p-5">
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
        <Mail className="w-5 h-5 text-primary" />
      </div>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-foreground">
            {customer.full_name || customer.email}
          </p>
          {blocked && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/15 text-destructive border border-destructive/30">
              Blocked
            </span>
          )}
        </div>
        {customer.full_name && (
          <p className="text-sm text-muted-foreground">{customer.email}</p>
        )}
        <p className="text-xs text-muted-foreground mt-0.5">
          {customer.order_count} order{customer.order_count !== 1 ? "s" : ""} · ${customer.total_spent.toFixed(0)} spent{customer.last_order ? ` · Last: ${new Date(customer.last_order).toLocaleDateString()}` : ""}
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2">
      {blocked && onUnblock ? (
        <Button size="sm" variant="outline" onClick={() => onUnblock(customer.email)}>
          <ShieldCheck className="w-4 h-4 mr-2" /> Unblock
        </Button>
      ) : onBlock ? (
        <Button size="sm" variant="ghost" onClick={() => onBlock(customer.email)}>
          <ShieldBan className="w-4 h-4 text-destructive" />
        </Button>
      ) : null}
      {onDelete && (
        <Button size="sm" variant="ghost" onClick={() => onDelete(customer.user_id, customer.email)}>
          <Trash2 className="w-4 h-4 text-destructive" />
        </Button>
      )}
    </div>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="bg-card rounded-2xl shadow-soft p-12 text-center">
    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
    <p className="text-muted-foreground">{message}</p>
  </div>
);

export default AdminCustomers;
